import { Section } from '@/shared/components/ui/Section'
import { Table } from '@/shared/components/ui/Table'
import { Badge } from '@/shared/components/ui/Badge'
import { useTranslation } from '@/shared/i18n/useTranslation'
import type { TranslateFn } from '@/shared/i18n/useTranslation'
import type { SurvivorStandings, SurvivorStandingEntry } from '../types'
import styles from './SurvivorStandingsCard.module.css'

type Variant = 'champion' | 'alive' | 'eliminated'

/**
 * Who is left. A real table rather than three styled lists: it is one column of
 * names against one column of state, and a screen reader should be able to say
 * so. Champions sort first, then the living, then the out.
 */
export function SurvivorStandingsCard({ standings }: { standings: SurvivorStandings }) {
  const { t } = useTranslation()

  const rows: { entry: SurvivorStandingEntry; variant: Variant }[] = [
    ...standings.champions.map((entry) => ({ entry, variant: 'champion' as const })),
    ...standings.alive.map((entry) => ({ entry, variant: 'alive' as const })),
    ...standings.eliminated.map((entry) => ({ entry, variant: 'eliminated' as const })),
  ]

  const meta = t('survivor.standings.meta', {
    alive: standings.champions.length + standings.alive.length,
    out:   standings.eliminated.length,
  })

  return (
    <Section label={t('survivor.standings.title')} meta={meta}>
      {rows.length === 0 ? (
        <p className={styles.empty}>{t('survivor.standings.alive')}</p>
      ) : (
        <Table label={t('survivor.standings.title')}>
          <tbody>
            {rows.map(({ entry, variant }) => (
              <Table.Row key={entry.participationId}>
                <Table.Cell className={variant === 'eliminated' ? styles.isOut : undefined}>
                  {entry.displayName}
                </Table.Cell>
                <Table.Cell className={styles.stateCell}>
                  <StateBadge entry={entry} variant={variant} t={t} />
                </Table.Cell>
              </Table.Row>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  )
}

function StateBadge({
  entry,
  variant,
  t,
}: {
  entry:   SurvivorStandingEntry
  variant: Variant
  t:       TranslateFn
}) {
  if (variant === 'champion') {
    return <Badge tone="money" variant="soft">{t('survivor.status.champion')}</Badge>
  }

  if (variant === 'alive') {
    return <Badge tone="alive" variant="outline" hasDot>{t('survivor.status.alive')}</Badge>
  }

  // An elimination is more useful with the round it happened in than with the
  // word "eliminated" repeated down the column.
  return (
    <Badge tone="out" variant="soft">
      {entry.eliminatedAtGameweekNumber !== undefined
        ? t('survivor.round.short', { number: entry.eliminatedAtGameweekNumber })
        : t('survivor.status.eliminated')}
    </Badge>
  )
}
